import { useEffect, type ReactNode } from 'react';
import { useReveal } from '../components/useReveal';
import './LegalDocument.css';

export interface LegalSection {
  heading: string;
  body: ReactNode;
}

interface LegalDocumentProps {
  title: string;
  intro: string;
  lastUpdated: string;
  sections: LegalSection[];
}

/**
 * Shared shell for every legal page. Feed it a title, intro, and a list of
 * numbered sections — it renders a consistent, auto-numbered document with
 * a jump-list table of contents. Mirrors the parkpursuit-marketing pattern,
 * dressed in the Arcadia neon.
 */
export function LegalDocument({ title, intro, lastUpdated, sections }: LegalDocumentProps) {
  useReveal(title);

  useEffect(() => {
    document.title = `${title} — ARCADIA '84`;
  }, [title]);

  return (
    <article className="legal">
      <div className="legal__glow" aria-hidden="true" />
      <header className="legal__head shell">
        <p className="eyebrow">Legal</p>
        <h1 className="legal__title">{title}</h1>
        <p className="legal__intro">{intro}</p>
        <p className="legal__date">Last updated: {lastUpdated}</p>
      </header>

      <div className="legal__body shell">
        <nav className="legal__toc" aria-label="Sections">
          <p className="legal__toc-head">CONTENTS</p>
          <ol>
            {sections.map((s, i) => (
              <li key={s.heading}>
                <a href={`#s-${i + 1}`}>{s.heading}</a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="legal__sections">
          {sections.map((s, i) => (
            <section key={s.heading} id={`s-${i + 1}`} className="legal__section reveal">
              <h2>
                <span className="legal__num">{String(i + 1).padStart(2, '0')}</span>
                {s.heading}
              </h2>
              <div className="legal__prose">{s.body}</div>
            </section>
          ))}
        </div>
      </div>
    </article>
  );
}
